/**
 * Accessible tabs (WAI-ARIA tabs pattern, automatic activation) for sections/voge-product-tabs.liquid.
 * Without JS every panel is shown in sequence with its own heading.
 */
class VogeTabs extends HTMLElement {
  connectedCallback() {
    this.list = /** @type {HTMLElement | null} */ (this.querySelector('[data-tablist]'));
    if (!this.list) return;
    this.tabs = /** @type {HTMLButtonElement[]} */ (Array.from(this.list.querySelectorAll('[role="tab"]')));
    if (!this.tabs.length) return;

    this.list.hidden = false;
    this.classList.add('is-enhanced');
    this.list.addEventListener('click', this.#onClick);
    this.list.addEventListener('keydown', this.#onKeydown);

    const hashId = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';
    const initial = this.tabs.find((tab) => hashId && tab.getAttribute('aria-controls') === hashId) || this.tabs[0];
    this.select(/** @type {HTMLButtonElement} */ (initial), false);
  }

  disconnectedCallback() {
    this.list?.removeEventListener('click', this.#onClick);
    this.list?.removeEventListener('keydown', this.#onKeydown);
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} [focus]
   */
  select(tab, focus = true) {
    for (const item of this.tabs || []) {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(item.getAttribute('aria-controls') || '')}`);
      if (panel instanceof HTMLElement) panel.hidden = !selected;
    }
    if (focus) tab.focus();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
    if (tab instanceof HTMLButtonElement) this.select(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tabs = this.tabs || [];
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (current < 0) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
        next = rtl ? current - 1 : current + 1;
        break;
      case 'ArrowLeft':
        next = rtl ? current + 1 : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = tabs[(next + tabs.length) % tabs.length];
    if (target) this.select(target);
  };
}

if (!customElements.get('voge-tabs')) customElements.define('voge-tabs', VogeTabs);